import { useMemo, useState } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Dropdown, Flex, Spin, Tooltip } from "antd"
import { CalendarOutlined, MoreOutlined, UserOutlined, UpOutlined, DownOutlined } from "@ant-design/icons"
import { DSIcon } from "ui/Icon/DSIcon"
import IconWithBg from "ui/IconWithBg"
import { useBreakpoints } from "modules/window/hooks/useBreakpoints"
import { notifyWaiver } from "store/digital-waiver"
import { numberToCurrency } from "helpers/string"
import { downloadFile } from "utils/file-helpers"
import { toaster } from "components/common/Toast"
import { NotifyModal } from "components/trips/TripsPreview/modal/Notify"
import CustomPagination from "components/common/CustomPagination"
import { CHECK_IN_STATUS_LABEL } from "../../../CheckIn/checkInUtils"
import BookingTag from "../../../BookingTag"
import WaiverStatusTag from "../WaiverStatusTag/WaiverStatusTag"
import AnswerAccordion from "../AnswerAccordion/AnswerAccordion"
import styles from "../../index.module.css"

// Rows-per-page options for the desktop waiver table (first entry is the default).
const WAIVER_PAGE_SIZES = [5, 10, 25]

// i18n keys for each guest-type count shown in the header breakdown.
const GUEST_TYPE_COUNT_KEY = {
  adult: "booking.reservation_detail.adults_count",
  child: "booking.reservation_detail.children_count",
  infant: "booking.reservation_detail.infants_count",
  passenger: "booking.reservation_detail.passengers_count"
}

// Read-only check-in artwork for the detail header — check-out icon once anyone is out (green when
// all out, orange while partial), otherwise check-in icon (grey pending / green active). Mirrors
// the listing badge's iconFor.
const renderCheckInIcon = (checkInState, checkedOutCount) => {
  if (checkInState === "out") return <DSIcon name="checkOut" variant="active" width={52} height={25} />
  if (checkedOutCount > 0) return <DSIcon name="checkOut" variant="partial" width={52} height={25} />
  return <DSIcon name="checkIn" variant={checkInState === "pending" ? "pending" : "active"} width={52} height={25} />
}

const ExperienceCard = ({
  experience = {},
  waivers = [],
  waiversLoading = false,
  answers = [],
  answersLoading = false,
  bookingUuid,
  onRefresh,
  isCompleted = false,
  checkInState = "pending",
  checkedInCount = 0,
  checkedOutCount = 0,
  totalGuests = 0
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const breakpoint = useBreakpoints()
  const isMobile = ["xs", "xsm", "sm"].includes(breakpoint)
  const [isExpanded, setIsExpanded] = useState(true)
  const [pageSize, setPageSize] = useState(WAIVER_PAGE_SIZES[0])
  const [page, setPage] = useState(1)
  const [notifyWaiverRecord, setNotifyWaiverRecord] = useState(null)
  // Id of the row whose signed copy is being fetched — its actions menu becomes a spinner. The
  // helper pulls the whole PDF as a blob before the browser's save dialog opens, so without this
  // a large file reads as a dead click.
  const [downloadingWaiverId, setDownloadingWaiverId] = useState(null)
  const { loading: isNotifying } = useSelector((store) => store.digitalWaiver?.notifyWaiver) || {}
  const signedCount = waivers.filter((waiver) => waiver.status === "Signed").length

  // Client-side pagination for the desktop waiver table. Clamp the page whenever the list or page
  // size changes so we never land on an out-of-range (empty) page.
  const pageCount = Math.max(1, Math.ceil(waivers.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const pageStart = (currentPage - 1) * pageSize
  const pagedWaivers = waivers.slice(pageStart, pageStart + pageSize)
  // Header docs badge uses the reservation-level docs count (mirrors the listing's Waivers/Docs
  // column); the Waivers & Documents section below still lists the actual documents.
  const docsSign = experience.docsSign ?? 0
  const docsTotal = experience.docsTotal ?? 0

  const toggleExpanded = () => setIsExpanded((prev) => !prev)

  const checkInIcon = renderCheckInIcon(checkInState, checkedOutCount)

  // Show every guest type present in the booking (adult/child/infant/passenger), not just adults.
  // Falls back to "N adults" when no breakdown is available.
  const guestBreakdown = experience.guestBreakdown ?? []
  const guestLabel = guestBreakdown.length
    ? guestBreakdown.map(({ type, count }) => t(GUEST_TYPE_COUNT_KEY[type] ?? GUEST_TYPE_COUNT_KEY.adult, { count })).join(", ")
    : t("booking.reservation_detail.adults_count", { count: experience.adults })

  // Guest count + check-in status + docs badge — shared by both header layouts.
  const statsRow = (
    <Flex align="center" gap={8} className={styles.expStats}>
      <Tooltip title={CHECK_IN_STATUS_LABEL[checkInState]}>
        {/* Read-only status on the detail page — check-in is done from the listings */}
        <span className={styles.checkInStatus}>
          {checkInIcon}
          {checkInState === "partial" && (
            <span className={styles.checkInCount}>
              {checkedOutCount > 0 ? `${checkedOutCount}/${totalGuests}` : `${checkedInCount}/${totalGuests}`}
            </span>
          )}
        </span>
      </Tooltip>
      <Flex align="center" gap={4}>
        <UserOutlined className={styles.metaIcon} />
        <span className={styles.metaText}>{guestLabel}</span>
      </Flex>
      {/* No documents attached to the booking — hide the badge instead of showing "0/0" */}
      {docsTotal > 0 && (
        <BookingTag color={docsSign === docsTotal ? "success" : "error"}>
          <DSIcon name="file" width={14} height={14} fill="currentColor" />
          {docsSign}/{docsTotal}
        </BookingTag>
      )}
    </Flex>
  )

  const downloadSignedCopy = async (waiver) => {
    setDownloadingWaiverId(waiver.id)
    try {
      await downloadFile(waiver.documentUrl, waiver.document || undefined)
    } finally {
      setDownloadingWaiverId(null)
    }
  }

  const handleWaiverAction = (key, waiver) => {
    if (key === "download") {
      // One row per document, so the row already names the file — download it directly rather
      // than asking again. The signed PDF is rendered asynchronously after signing, so a row can
      // read "Signed" before its copy exists; say so instead of serving the blank template.
      if (waiver.documentUrl) {
        downloadSignedCopy(waiver)
      } else {
        toaster.error({ title: t("booking.reservation_detail.signed_copy_pending") })
      }
      return
    }
    if (key === "resend" && bookingUuid && !isCompleted) setNotifyWaiverRecord(waiver)
  }

  // Notify the guest by email or text. The modal reads snake_case guest data (shared with the
  // legacy booking details screen), so map the waiver row back into that shape.
  // Memoized so the modal sees a stable object — a new one each render re-triggers its pre-fill.
  const notifyGuestData = useMemo(
    () =>
      notifyWaiverRecord && {
        is_primary_guest: notifyWaiverRecord.isPrimaryGuest,
        share_by: notifyWaiverRecord.shareBy,
        shared_email: notifyWaiverRecord.sharedEmail,
        shared_phone: notifyWaiverRecord.sharedPhone,
        guest_email: notifyWaiverRecord.guestEmail,
        phone_number: notifyWaiverRecord.phone,
        notify_details: notifyWaiverRecord.notifyDetails
      },
    [notifyWaiverRecord]
  )

  const handleNotifyGuest = (values) => {
    const payload = { notify_by: values.sendMethod }
    if (notifyWaiverRecord?.sharedWaiverId) payload.shared_waiver_id = notifyWaiverRecord.sharedWaiverId
    if (values.sendMethod === "email") {
      payload.email = values.email
    } else {
      payload.phone_number = values.phoneNumber?.fullNumber
    }
    dispatch(notifyWaiver(bookingUuid, payload)).then((res) => {
      toaster.success({ title: res?.message || t("booking.reservation_detail.waiver_request_sent") })
      setNotifyWaiverRecord(null)
      onRefresh?.()
    })
  }

  // Resend is pointless once the reservation is completed — the trip has already happened — so
  // the item stays visible but disabled, with a tooltip explaining why (antd disables pointer
  // events on a disabled item, hence the re-enabling wrapper class on the label).
  const resendItem = isCompleted
    ? {
        key: "resend",
        disabled: true,
        label: (
          <Tooltip title={t("booking.reservation_detail.resend_disabled_completed")} placement="left">
            <span className={styles.menuItemLabel}>{t("booking.reservation_detail.resend")}</span>
          </Tooltip>
        )
      }
    : { key: "resend", label: t("booking.reservation_detail.resend") }

  // Row/card actions menu (download signed waivers, resend unsigned) — shared by table + mobile
  // cards. While this row's file is downloading the menu is replaced by a spinner, since the
  // dropdown closes on click and would otherwise leave the wait unacknowledged.
  const renderWaiverActions = (waiver) =>
    downloadingWaiverId === waiver.id ? (
      <Tooltip title={t("booking.reservation_detail.downloading")}>
        <Spin size="small" />
      </Tooltip>
    ) : (
      <Dropdown
        trigger={["click"]}
        placement="bottomRight"
        menu={{
          onClick: ({ key }) => handleWaiverAction(key, waiver),
          items:
            waiver.status === "Signed"
              ? [{ key: "download", label: t("booking.reservation_detail.download") }]
              : [resendItem]
        }}
      >
        <button type="button" className={styles.iconBtn}>
          <MoreOutlined />
        </button>
      </Dropdown>
    )

  return (
    <div className={styles.card}>
      {/* Experience section — mobile stacks the details under the code/tag row with the total inline */}
      {isMobile ? (
        <Flex align="flex-start" gap={12}>
          <img src={experience.imageUrl} alt={experience.title} className={styles.expImage} />
          <Flex vertical gap={4} className={styles.expBody}>
            <Flex align="center" justify="space-between" gap={8}>
              <Flex align="center" gap={8}>
                <span className={styles.expCode}>{experience.id}</span>
                <BookingTag color="blue">{experience.status}</BookingTag>
              </Flex>
              <IconWithBg icon={isExpanded ? <UpOutlined /> : <DownOutlined />} onClick={toggleExpanded} />
            </Flex>
            <span className={styles.expTitle}>{experience.title}</span>
            <Flex align="center" gap={6} className={styles.expMeta}>
              <CalendarOutlined className={styles.metaIcon} />
              <span className={styles.metaText}>
                {experience.date}
                {experience.timeStart && ` · ${[experience.timeStart, experience.timeEnd].filter(Boolean).join(" - ")}`}
              </span>
            </Flex>
            <Flex align="center" gap={4}>
              <UserOutlined className={styles.metaIcon} />
              <span className={styles.metaText}>{t("booking.reservation_detail.adults_count", { count: experience.adults })}</span>
            </Flex>
            <Flex align="center" gap={6}>
              <span className={styles.expTotalLabel}>{t("booking.reservation_detail.total")}</span>
              <span className={styles.expTotalInline}>{numberToCurrency(experience.total ?? 0)}</span>
            </Flex>
            {statsRow}
          </Flex>
        </Flex>
      ) : (
        <Flex align="flex-start" justify="space-between" gap={12}>
          <Flex align="flex-start" gap={12}>
            <img src={experience.imageUrl} alt={experience.title} className={styles.expImage} />
            <Flex vertical gap={4}>
              <Flex align="center" gap={8}>
                <span className={styles.expTitle}>{experience.title}</span>
              </Flex>
              <Flex align="center" gap={6} className={styles.expMeta}>
                <span className={styles.expCode}>{experience.id}</span>
                <span className={styles.expDot}>·</span>
                <CalendarOutlined className={styles.metaIcon} />
                <span className={styles.metaText}>
                  {experience.date}
                  {experience.timeStart && ` · ${[experience.timeStart, experience.timeEnd].filter(Boolean).join(" - ")}`}
                </span>
              </Flex>
              {statsRow}
            </Flex>
          </Flex>
          <Flex align="flex-end" gap={8} className={styles.expTotalCol}>
            <Flex vertical gap={6}>
              <span className={styles.expTotalLabel}>{t("booking.reservation_detail.total")}</span>
              <span className={styles.expTotal}>{numberToCurrency(experience.total ?? 0)}</span>
            </Flex>
            <IconWithBg icon={isExpanded ? <UpOutlined /> : <DownOutlined />} onClick={toggleExpanded} />
          </Flex>
        </Flex>
      )}

      {isExpanded && (
        <>
          {/* Waivers & Documents section */}
          <div className={styles.sectionDivider} />
          <Flex align="center" justify="space-between" className={styles.cardHeader}>
            <span className={styles.cardTitle}>
              {t("booking.reservation_detail.waivers_documents", { signed: signedCount, total: waivers.length })}
            </span>
          </Flex>
          {isMobile ? (
            <Flex vertical gap={12} className={styles.waiverCardList}>
              {waiversLoading && waivers.length === 0 && (
                <Flex justify="center" className="p-m">
                  <Spin size="small" />
                </Flex>
              )}
              {!waiversLoading && waivers.length === 0 && (
                <span className={styles.emailCell}>{t("booking.reservation_detail.no_waivers", "No waivers added.")}</span>
              )}
              {waivers.map((waiver) => (
                <div key={waiver.id} className={styles.waiverCard}>
                  <Flex align="center" justify="space-between" gap={8}>
                    <Flex align="center" gap={8} className={styles.waiverCardHead}>
                      <DSIcon name="file" width={16} height={16} />
                      <span className={styles.waiverCardDoc}>{waiver.document}</span>
                      <WaiverStatusTag status={waiver.status} />
                    </Flex>
                    {renderWaiverActions(waiver)}
                  </Flex>
                  <span className={styles.waiverCardName}>
                    {waiver.fullName}
                    {waiver.age != null && waiver.age !== "" && `, ${t("booking.reservation_detail.waiver_age", { age: waiver.age })}`}
                  </span>
                  <span className={styles.waiverCardContact}>
                    {[waiver.guestEmail, waiver.phone && waiver.phone !== "-" ? waiver.phone : null].filter(Boolean).join(" · ")}
                  </span>
                </div>
              ))}
            </Flex>
          ) : (
            <div className={styles.waiverTableWrap}>
              <table className={styles.waiverTable}>
                <thead>
                  <tr>
                    <th>{t("booking.reservation_detail.col_full_name")}</th>
                    <th>{t("booking.reservation_detail.col_guest_email")}</th>
                    <th>{t("booking.reservation_detail.col_phone")}</th>
                    <th>{t("booking.reservation_detail.col_document")}</th>
                    <th>
                      {t("booking.reservation_detail.col_age")} <span className={styles.sortIcon}>↕</span>
                    </th>
                    <th>
                      {t("booking.reservation_detail.col_status")} <span className={styles.sortIcon}>↕</span>
                    </th>
                    <th>{t("booking.reservation_detail.col_actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {waiversLoading && waivers.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <Flex justify="center" className="p-m">
                          <Spin size="small" />
                        </Flex>
                      </td>
                    </tr>
                  )}
                  {!waiversLoading && waivers.length === 0 && (
                    <tr>
                      <td colSpan={7} className={styles.emailCell}>
                        {t("booking.reservation_detail.no_waivers", "No waivers added.")}
                      </td>
                    </tr>
                  )}
                  {pagedWaivers.map((waiver) => (
                    <tr key={waiver.id}>
                      <td>{waiver.fullName}</td>
                      <td className={styles.emailCell}>{waiver.guestEmail}</td>
                      <td>{waiver.phone ?? "-"}</td>
                      <td>
                        <Flex align="center" gap={4}>
                          <DSIcon name="file" width={14} height={14} />
                          <span>{waiver.document}</span>
                        </Flex>
                      </td>
                      <td>{waiver.age}</td>
                      <td>
                        <WaiverStatusTag status={waiver.status} />
                      </td>
                      <td>{renderWaiverActions(waiver)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isMobile && waivers.length > 0 && (
            <div className={styles.waiverPagination}>
              <CustomPagination
                metadata={{
                  page: currentPage,
                  total_pages: pageCount,
                  prev_page: currentPage > 1 ? currentPage - 1 : null,
                  next_page: currentPage < pageCount ? currentPage + 1 : null
                }}
                pageSize={pageSize}
                total={waivers.length}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
                pageSizeOptions={WAIVER_PAGE_SIZES}
              />
            </div>
          )}

          {/* Answers & Preferences section */}
          <div className={styles.sectionDivider} />
          <span className={styles.cardTitle}>{t("booking.reservation_detail.answers_preferences")}</span>
          <Flex vertical gap={1} className={styles.accordionList}>
            {answersLoading && answers.length === 0 && (
              <Flex justify="center" className="p-m">
                <Spin size="small" />
              </Flex>
            )}
            {!answersLoading && answers.length === 0 && (
              <span className={styles.emailCell}>{t("booking.reservation_detail.no_answers", "No answers submitted.")}</span>
            )}
            {answers.map((item) => (
              <AnswerAccordion key={item.id} item={item} />
            ))}
          </Flex>
        </>
      )}

      <NotifyModal
        visible={!!notifyWaiverRecord}
        guestData={notifyGuestData}
        loading={isNotifying}
        isRestrictedToAvailableMethods
        onClose={() => setNotifyWaiverRecord(null)}
        onSend={handleNotifyGuest}
      />
    </div>
  )
}

ExperienceCard.propTypes = {
  experience: PropTypes.object,
  waivers: PropTypes.array,
  waiversLoading: PropTypes.bool,
  answers: PropTypes.array,
  answersLoading: PropTypes.bool,
  bookingUuid: PropTypes.string,
  onRefresh: PropTypes.func,
  isCompleted: PropTypes.bool,
  checkInState: PropTypes.oneOf(["pending", "partial", "in", "out"]),
  checkedInCount: PropTypes.number,
  checkedOutCount: PropTypes.number,
  totalGuests: PropTypes.number
}

export default ExperienceCard
